import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  Typography,
  Box,
  Button,
  IconButton,
  MenuItem,
  TextField
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Breadcrumb from 'src/components/Breadcrumb';
import { useDispatch, useSelector } from 'react-redux';
import useDebounce from 'src/hooks/useDebounce';
import {
  downloadCFDIList,
  loadCFDIS,
  setCFDIsOptions,
  syncInvoicesAction
} from 'src/store/actions/cfdiActions';
import {
  ChevronLeft,
  ChevronRight,
  GetApp as Download,
  Loop as LoopIcon
} from '@mui/icons-material';
import { selectCFDISQueryOptions } from 'src/store/selectors/cfdiSelectors';
import { InvoiceTypes, IssuerType } from 'src/utils/constants/cfdi';
import { loadParameters } from 'src/store/actions/parameterActions';
import { ParameterSection } from 'src/utils/constants/parameters';
import { selectParameterList } from 'src/store/selectors/parameterSelector';
import {
  BillingSubSectionList,
  filterParameterByBillingSubSection
} from 'src/views/parameter/ParameterListView/BillingParameter/BillingParameterList';
import SearchBarByType from 'src/components/SearchBarByType';
import { CLEAR_CFDIS_OPTIONS, SYNC_INVOICES } from 'src/store/constants/CFDI';
import selectFormatDate from 'src/utils/selectDateFormat';
import dayjs from 'src/utils/dayjs';
import dateFormatType from 'src/utils/dateFormatType';
import MenuButton from 'src/components/MenuButton/MenuButton';
import { CfdiActions } from 'src/utils/constants/clients';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import ConfirmationDialog from 'src/components/ConfirmDialog/ConfirmationDialog';
import { selectAuthRole } from 'src/store/selectors/authSelectors';
import { UserRoles } from 'src/utils/constants/auth';
import useStyles from './styles';
import DownloadInvoicesListDialog from './DownloadInvoicesListDialog/DownloadInvoicesListDialog';

const searchTypes = [
  { value: 'client', label: 'NAME', type: 'text' },
  { value: 'folio', label: 'FOLIO', type: 'text' },
  { value: 'rfc', label: 'RFC', type: 'text' }
];
const CFDIMenu = [
  {
    label: 'COMMON.DOWNLOAD_INVOICES',
    key: CfdiActions.DOWNLOAD,
    icon: <Download />
  },
  {
    label: 'COMMON.DOWNLOAD_CFDIS',
    key: CfdiActions.DOWNLOAD_CFDI,
    icon: <Download />
  }
];
const selectCFDIActions = () => {
  const actions = CFDIMenu.filter(item => {
    return [CfdiActions.DOWNLOAD, CfdiActions.DOWNLOAD_CFDI].includes(item.key);
  });
  return actions;
};

const typeOptions = [
  {
    label: 'ENUMS.INVOICE_TYPES.EXPENSE',
    value: InvoiceTypes.EXPENSE
  },
  {
    label: 'ENUMS.INVOICE_TYPES.INCOME',
    value: InvoiceTypes.INCOME
  },
  {
    label: 'ENUMS.INVOICE_TYPES.PAYMENT',
    value: InvoiceTypes.PAYMENT
  }
];

// The filtered month must be labelled from its own digits, not from
// `new Date(year, month - 1, 1)`: that builds midnight in the browser's
// timezone, and momentLocalTimezone then re-anchors the instant to Mexico
// City, which rolls it back to the previous month for any browser east of
// UTC-6.
const monthFilterDate = (year, month) =>
  `${year}-${String(month).padStart(2, '0')}-01`;

// Mexico City wall-clock, so the chip and the query agree on which month is
// current no matter where the browser is.
const currentMonth = () => dayjs.tz().startOf('month');

const Toolbar = ({ className, ...rest }) => {
  const [searchValue, setSearchValue] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [seriesFilter, setSeriesFilter] = useState('');
  const [dateFilter, setDateFilter] = useState(() => currentMonth());
  const [searchCriteria, setSearchCriteria] = useState('client');
  const [openDownloadInvoices, setOpenDownloadInvoices] = useState(false);
  const [openSyncDialog, setOpenSyncDialog] = useState(false);
  const userRole = useSelector(selectAuthRole);

  const { t } = useTranslation();
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const queryOptions = useSelector(selectCFDISQueryOptions);
  const parameters = useSelector(selectParameterList);
  const isLoadingSync = useSelector(isLoadingSelector([SYNC_INVOICES.action]));
  const seriesOptions = filterParameterByBillingSubSection(
    parameters,
    BillingSubSectionList.INVOICE_TYPES
  ).map(parameter => {
    return {
      label: parameter.value,
      value: parameter.value
    };
  });

  const debounceSearch = useDebounce((search, queryOpts) => {
    if (search.rfc || search.folio || search.client) {
      dispatch(
        loadCFDIS({ ...queryOpts, ...search, page: 0, month: null, year: null })
      );
    } else {
      dispatch(
        loadCFDIS({
          ...queryOpts,
          client: '',
          rfc: '',
          folio: '',
          page: 0,
          month: queryOpts.month ?? new Date().getMonth() + 1,
          year: queryOpts.year ?? new Date().getFullYear()
        })
      );
    }
  }, 400);

  const handleCloseDownloadInvoices = () => {
    setOpenDownloadInvoices(false);
  };

  const handleOpenSyncDialog = () => {
    setOpenSyncDialog(true);
  };

  const handleCloseSyncDialog = () => {
    setOpenSyncDialog(false);
  };

  const handleSync = () => {
    const closeCallback = () => {
      handleCloseSyncDialog();
    };

    dispatch(
      syncInvoicesAction(
        {
          year: queryOptions.year,
          month: queryOptions.month
        },
        closeCallback
      )
    );
  };

  const handleSearch = useCallback(
    (e, criteria) => {
      const { value } = e.target;
      if (!value) {
        setSearchValue('');
        debounceSearch({ [criteria]: '' }, queryOptions);
        return;
      }

      setSearchValue(value);
      setSearchCriteria(criteria);
      debounceSearch({ [criteria]: value }, queryOptions);
    },
    [setSearchValue, debounceSearch, searchCriteria]
  );

  const handleSearchCriteria = useCallback(() => {
    setSearchValue('');
    dispatch(
      setCFDIsOptions({
        ...queryOptions,
        [searchCriteria]: ''
      })
    );
  }, [dispatch, queryOptions]);

  const applyFilters = useCallback(
    ({ type = typeFilter, series = seriesFilter, date = dateFilter }) => {
      debounceSearch(
        {},
        {
          ...queryOptions,
          page: 0,
          type: type || undefined,
          series: series || undefined,
          month: date ? date.month() + 1 : null,
          year: date ? date.year() : null
        }
      );
    },
    [debounceSearch, queryOptions, typeFilter, seriesFilter, dateFilter]
  );

  const handleTypeChange = event => {
    const { value } = event.target;
    setTypeFilter(value);
    applyFilters({ type: value });
  };

  const handleSeriesChange = event => {
    const { value } = event.target;
    setSeriesFilter(value);
    applyFilters({ series: value });
  };

  const handleDateChange = value => {
    setDateFilter(value);
    applyFilters({ date: value });
  };

  const handleMonthStep = delta => {
    handleDateChange(dateFilter.add(delta, 'month'));
  };

  const hasActiveFilters = Boolean(
    typeFilter ||
      seriesFilter ||
      searchValue ||
      !dateFilter.isSame(currentMonth(), 'month')
  );

  const handleClearFilters = () => {
    setTypeFilter('');
    setSeriesFilter('');
    setDateFilter(currentMonth());
    setSearchValue('');
    debounceSearch(
      {},
      {
        ...queryOptions,
        page: 0,
        type: undefined,
        series: undefined,
        month: null,
        year: null
      }
    );
  };

  useEffect(() => {
    dispatch(
      loadCFDIS({
        ...queryOptions,
        issuer: IssuerType.ISSUING,
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear()
      })
    );
    dispatch(
      loadParameters({
        section: ParameterSection.BILLING
      })
    );

    return () => {
      dispatch({ type: CLEAR_CFDIS_OPTIONS });
    };
  }, []);

  const handleClickMore = option => {
    if (option === CfdiActions.DOWNLOAD) {
      dispatch(
        downloadCFDIList({
          month: queryOptions.month ?? new Date().getMonth() + 1,
          year: queryOptions.year ?? new Date().getFullYear()
        })
      );
    }

    if (option === CfdiActions.DOWNLOAD_CFDI) {
      setOpenDownloadInvoices(true);
    }
  };

  return (
    <>
      <Box className={clsx(classes.root, className)} {...rest}>
        <Breadcrumb doNotRedirectIndexList={[0]} />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography
              style={{ fontWeight: '400', fontSize: '1.5rem' }}
              color="primary"
              variant="h1"
            >
              {t('NAVIGATION.CFDI_ISSUED')}
            </Typography>
          </Box>
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between'
              }}
            >
              {userRole === UserRoles.SUPER_ADMIN && (
                <Button
                  className={clsx(
                    classes.buttons,
                    classes.outlined,
                    classes.horizontalPadding
                  )}
                  sx={{ marginRight: 2, marginLeft: 2 }}
                  color="primary"
                  onClick={handleOpenSyncDialog}
                >
                  <LoopIcon className={classes.icon} />
                  {t('COMMON.SYNC')}
                </Button>
              )}
            </Box>
            <DownloadInvoicesListDialog
              open={openDownloadInvoices}
              onClose={handleCloseDownloadInvoices}
            />
            <MenuButton
              icon={<Download />}
              options={selectCFDIActions()}
              handleClickOption={handleClickMore}
              deleteDisabled
            />
          </Box>
        </Box>
        <Box className={classes.filters}>
          <Box className={classes.searchSlot}>
            <SearchBarByType
              types={searchTypes}
              handleSearch={handleSearch}
              searchValue={searchValue}
              handleSearchCriteria={handleSearchCriteria}
            />
          </Box>
          <Box className={classes.spacer} />
          <Box className={classes.monthChip}>
            <IconButton
              className={classes.monthArrow}
              aria-label={t('COMMON.PREVIOUS_MONTH')}
              onClick={() => handleMonthStep(-1)}
            >
              <ChevronLeft />
            </IconButton>
            <DatePicker
              value={dateFilter}
              locale="en"
              views={['year', 'month']}
              minDate={dayjs.tz().subtract(5, 'years')}
              onChange={handleDateChange}
              slotProps={{
                textField: {
                  variant: 'standard',
                  className: classes.monthField,
                  inputProps: { readOnly: true },
                  InputProps: { disableUnderline: true, autoComplete: 'off' },
                  onFocus: e => e.target.blur(),
                  onKeyDown: e => e.preventDefault()
                }
              }}
            />
            <IconButton
              className={classes.monthArrow}
              aria-label={t('COMMON.NEXT_MONTH')}
              onClick={() => handleMonthStep(1)}
            >
              <ChevronRight />
            </IconButton>
          </Box>
          <TextField
            select
            size="small"
            label={t('COMMON.TYPE')}
            className={classes.filterField}
            value={typeFilter}
            onChange={handleTypeChange}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="">{t('COMMON.ALL')}</MenuItem>
            {typeOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {t(option.label)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={t('COMMON.SERIE')}
            className={classes.filterField}
            value={seriesFilter}
            onChange={handleSeriesChange}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="">{t('COMMON.ALL')}</MenuItem>
            {seriesOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <Button
            className={clsx(classes.outlined, classes.clearButton)}
            color="primary"
            onClick={handleClearFilters}
            disabled={!hasActiveFilters}
          >
            {t('COMMON.CLEAR_FILTERS')}
          </Button>
        </Box>
      </Box>

      <ConfirmationDialog
        open={openSyncDialog}
        handleClose={handleCloseSyncDialog}
        onSubmit={handleSync}
        isLoading={isLoadingSync}
        title={t('CFDI_MANUAL_SYNC.TITLE')}
        description={t('CFDI_MANUAL_SYNC.DESCRIPTION', {
          month: selectFormatDate(
            monthFilterDate(queryOptions.year, queryOptions.month),
            dateFormatType.MONTH_AND_YEAR
          )
        })}
      />
    </>
  );
};

Toolbar.propTypes = {
  className: PropTypes.string
};

export default Toolbar;
